import axios from 'axios';
import { getIdToken } from '@/pages/MyAccount/components/firebaseIdToken';
import { NO_TOKEN_MESSAGE } from '@/api/customerHealth';

/**
 * Outreach email wording (Super Admin only) — the three routes of teamio-serverless-api/lifecycle-service
 * (plan docs/plans/2026-10-09-customer-lifecycle-phase1-plan.md, sections 15 and 16):
 *
 *   POST /get-outreach-templates   every email template: its variable chips, its latest saved version, its starting text
 *   POST /preview-outreach         the exact email for one practice, whether it would qualify today, how many would get it
 *   POST /save-outreach-template   store the typed words as the next numbered version (saving is the approval)
 *   POST /send-test-outreach       the Send button: audience 'me' = the typed words to the SIGNED-IN person's own address;
 *                                  audience 'practice' = the SAVED words to the practice's Admins
 *                                  (the route keeps its old name: the shared API Gateway has no room for a new one)
 *
 * THERE IS NO DEFAULT AUDIENCE. sendOutreach() below rejects anything but exactly 'me' or 'practice' before any request.
 * No call carries an address: for 'me' the server takes it from the verified token, for 'practice' it reads the
 * practice's own Admins. A practice send also needs the server switch, which is OFF (it answers with a plain refusal).
 *
 * THE SERVER IS THE GATE. It verifies this Firebase ID token and checks it against its own Super Admin allowlist before
 * it touches the database, and it records WHO saved or tested from the verified token, so this module never sends an
 * author or an address. Like api/customerHealth.js, every call FAILS CLOSED: with no token it does not call at all.
 */

/** A preview can build the whole health report cold (the server's deadline is 20 s; API Gateway cuts off at 29 s). */
export const OUTREACH_TIMEOUT_MS = 35000;

async function post(path, body, auth) {
  const idToken = await getIdToken(auth);
  if (!idToken) throw new Error(NO_TOKEN_MESSAGE);
  const { data } = await axios.post(path, body, {
    headers: { Authorization: `Bearer ${idToken}` },
    timeout: OUTREACH_TIMEOUT_MS,
    crossdomain: true,
  });
  return data;
}

export function fetchOutreachTemplates({ auth } = {}) {
  return post('/get-outreach-templates', {}, auth);
}

export function previewOutreach({ auth, templateKey, siteId, subject, body } = {}) {
  return post('/preview-outreach', { templateKey, siteId, subject, body }, auth);
}

export function saveOutreachTemplate({ auth, templateKey, subject, body } = {}) {
  return post('/save-outreach-template', { templateKey, subject, body }, auth);
}

/**
 * The Send button (plan section 21). `audience` must be EXACTLY 'me' or 'practice'; anything else rejects BEFORE any
 * request, so a missing or mistyped audience can never reach a practice by omission. The body is built per audience:
 *   'me'        template, practice and the typed words (the server emails them to the signed-in person only)
 *   'practice'  template, practice and the two numbers the person confirmed, and NOTHING else: no words (the server sends
 *               the SAVED version), no address (it reads the practice's own Admins)
 */
export function sendOutreach({ auth, audience, templateKey, siteId, subject, body, expectedRecipients, expectedVersion } = {}) {
  if (audience === 'me') return post('/send-test-outreach', { audience: 'me', templateKey, siteId, subject, body }, auth);
  if (audience === 'practice') return post('/send-test-outreach', { audience: 'practice', templateKey, siteId, expectedRecipients, expectedVersion }, auth);
  return Promise.reject(new Error('Choose who this goes to. Nothing was sent.'));
}
